import React from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import MediaCoverage from '../components/MediaCoverage';
import { 
  Palette, TrendingUp, MessageCircle, BarChart3, CheckCircle, 
  Star, Users, Calendar, Globe, ArrowRight, Sparkles, Heart,
  Eye, Mail, BadgeCheck, Target, Newspaper, Quote, ExternalLink, Award
} from 'lucide-react';

export default function WorkshopLandingPage() {
  const estadisticas = [
    { valor: '+500', label: 'Citas agendadas', icon: Calendar },
    { valor: '+30.000', label: 'Visitas mensuales', icon: Eye },
    { valor: '+5.000', label: 'Suscriptores', icon: Mail },
    { valor: 'Único', label: 'Directorio en Chile', icon: Award }
  ];

  const benefits = [
    {
      icon: Award,
      title: 'Directorio Especializado Único',
      description: 'El único directorio en Chile dedicado a talleres terapéuticos para personas en recuperación de adicciones.'
    },
    {
      icon: Users,
      title: 'Público Cualificado',
      description: 'Nuestros visitantes son familias y personas en recuperación buscando actividades terapéuticas complementarias.'
    },
    {
      icon: TrendingUp,
      title: '+30.000 Visitas Mensuales',
      description: 'Miles de personas buscan talleres de yoga, mindfulness, arte y otras terapias cada mes.'
    },
    {
      icon: MessageCircle,
      title: 'Contacto Directo',
      description: 'Recibe mensajes, llamadas y contactos por WhatsApp de personas interesadas en tu taller.'
    },
    {
      icon: Heart,
      title: 'Creado por Personas en Recuperación',
      description: 'Nuestra plataforma fue construida por quienes entienden el valor de las terapias complementarias en la recuperación.'
    },
    {
      icon: Star,
      title: 'Reseñas y Calificaciones',
      description: 'Construye tu reputación con opiniones de participantes satisfechos que valoran tu trabajo.'
    }
  ];

  const workshopTypes = [
    { name: 'Yoga y Meditación', icon: '🧘' },
    { name: 'Mindfulness', icon: '☮️' },
    { name: 'Teatro Terapéutico', icon: '🎭' },
    { name: 'Arteterapia', icon: '🎨' },
    { name: 'Musicoterapia', icon: '🎵' },
    { name: 'Danza/Movimiento', icon: '💃' },
    { name: 'Terapia Grupal', icon: '👥' },
    { name: 'Deportes/Trekking', icon: '🏃' }
  ];

  const pasos = [
    { num: '1', title: 'Regístrate', desc: 'Crea tu cuenta gratis' },
    { num: '2', title: 'Completa tu perfil', desc: 'Agrega fotos y descripción' },
    { num: '3', title: 'Define horarios', desc: 'Configura disponibilidad' },
    { num: '4', title: '¡Listo!', desc: 'Comienza a recibir inscripciones' }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Registra tu Taller Terapéutico | SinAdicciones.org - El Único Directorio de Chile</title>
        <meta name="description" content="Publica tu taller de yoga, teatro, mindfulness, arte o danza terapéutica en el único directorio de adicciones de Chile. +30.000 visitas mensuales, +500 citas agendadas." />
        <meta name="keywords" content="registrar taller, taller terapéutico Chile, yoga rehabilitación, mindfulness adicciones, arteterapia, danzaterapia" />
        <link rel="canonical" href="https://sinadicciones.org/registrar-taller" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/registrar-taller" />
        <meta property="og:title" content="Registra tu Taller Terapéutico | El Único Directorio de Chile" />
        <meta property="og:description" content="Publica tu taller de yoga, teatro, mindfulness, arte o danza terapéutica en el único directorio de adicciones de Chile. +30.000 visitas mensuales." />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Registra tu Taller Terapéutico" />
        <meta name="twitter:description" content="Publica tu taller en el único directorio especializado de Chile." />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
      
      
      <Header />
      
      {/* Hero Section */}
      <main className="pt-24">
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-slate-900 to-slate-950"></div>
          <div className="absolute top-20 left-20 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
          
          <div className="max-w-6xl mx-auto px-4 md:px-8 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 border border-purple-500/40 rounded-full mb-6">
                <Palette className="w-4 h-4 text-purple-400" />
                <span className="text-purple-400 font-semibold">DIRECTORIO DE TALLERES</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-50 mb-6 leading-tight">
                Publica tu <span className="text-purple-400">Taller</span> en el<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                  Único Directorio de Chile
                </span>
              </h1>
              
              <p className="text-xl text-slate-300 mb-4">
                Conecta con miles de personas buscando talleres de yoga, teatro, mindfulness, 
                arte y otras actividades terapéuticas.
              </p>
              
              <p className="text-lg text-slate-400 mb-8">
                Nuestro público: <strong className="text-purple-400">Personas en recuperación y familias</strong> buscando 
                actividades complementarias para el proceso de sanación.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button 
                  asChild
                  size="lg"
                  className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-xl px-8 py-6 text-lg font-semibold shadow-lg"
                >
                  <Link to="/login">
                    <Palette className="w-5 h-5 mr-2" />
                    Registrar mi Taller Gratis
                  </Link>
                </Button>
                <Button 
                  asChild
                  size="lg" 
                  variant="outline" 
                  className="border-slate-700 text-slate-300 hover:bg-slate-800 rounded-xl"
                >
                  <Link to="/talleres">
                    Ver Directorio de Talleres
                  </Link>
                </Button>
              </div>

              <div className="flex items-center justify-center gap-6 text-sm text-slate-400 flex-wrap">
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-400" />
                  Registro gratuito
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-400" />
                  Contacto directo
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-400" />
                  Estadísticas incluidas
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Estadísticas */}
        <section className="py-12 bg-slate-900/50 border-y border-slate-800">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {estadisticas.map((stat, idx) => {
                const Icon = stat.icon;
                return (
                  <div key={idx} className="text-center">
                    <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mx-auto mb-3">
                      <Icon className="w-6 h-6 text-purple-400" />
                    </div>
                    <p className="text-3xl md:text-4xl font-bold text-slate-50 mb-1">{stat.valor}</p>
                    <p className="text-sm text-slate-400">{stat.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Tipos de Talleres */}
        <section className="py-16 border-b border-slate-800">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-50 mb-4">
                ¿Qué tipo de <span className="text-purple-400">taller</span> ofreces?
              </h2>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {workshopTypes.map((type, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-slate-800 rounded-full hover:border-purple-500/50 transition-colors"
                >
                  <span className="text-xl">{type.icon}</span>
                  <span className="text-slate-300 text-sm">{type.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Beneficios */}
        <section className="py-20 border-b border-slate-800">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-50 mb-4">
                ¿Por qué publicar en <span className="text-purple-400">SinAdicciones</span>?
              </h2>
              <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                Beneficios exclusivos para talleres del directorio
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((b, idx) => {
                const Icon = b.icon;
                return (
                  <Card key={idx} className="bg-slate-900 border-slate-800 hover:border-purple-500/50 transition-all group">
                    <CardContent className="p-6">
                      <div className="w-14 h-14 rounded-2xl bg-purple-500/10 flex items-center justify-center mb-4 group-hover:bg-purple-500/20 transition-colors">
                        <Icon className="w-7 h-7 text-purple-400" />
                      </div>
                      <h3 className="font-semibold text-slate-50 text-lg mb-2">{b.title}</h3>
                      <p className="text-slate-400">{b.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Nuestra Misión */}
        <section className="py-20 bg-gradient-to-b from-slate-900/50 to-slate-950 border-b border-slate-800">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-purple-400" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-50 mb-4">
                Nuestra <span className="text-purple-400">Misión</span>
              </h2>
            </div>
            
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 text-center">
              <Quote className="w-10 h-10 text-purple-500/30 mx-auto mb-4" />
              <p className="text-xl md:text-2xl text-slate-200 leading-relaxed mb-6 italic">
                "Acortar el camino entre la familia que busca ayuda y el lugar correcto para encontrarla, 
                con información verificada, orientación honesta y la credibilidad de quienes lo vivieron desde adentro."
              </p>
              <p className="text-slate-400">
                Las terapias complementarias son una parte fundamental del proceso de recuperación. 
                Tu taller puede marcar la diferencia.
              </p>
            </div>
          </div>
        </section>

        {/* Lo que dicen los medios */}
        <section className="py-20 border-b border-slate-800">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 border border-blue-500/40 rounded-full mb-4">
                <Newspaper className="w-4 h-4 text-blue-400" />
                <span className="text-blue-400 font-semibold">EN LOS MEDIOS</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-50 mb-4">
                Lo que Dicen de <span className="text-blue-400">Nosotros</span>
              </h2>
            </div>

            <MediaCoverage />
          </div>
        </section>

        {/* Pasos */}
        <section className="py-20 border-b border-slate-800">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-50 mb-4">
                Comienza en <span className="text-purple-400">4 Pasos</span>
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {pasos.map((p, idx) => (
                <div key={idx} className="text-center">
                  <div className="w-16 h-16 rounded-full bg-purple-500 text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                    {p.num}
                  </div>
                  <h4 className="font-semibold text-slate-50 mb-1">{p.title}</h4>
                  <p className="text-sm text-slate-400">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
            <Palette className="w-16 h-16 text-purple-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-slate-50 mb-4">
              Tu Taller Puede <span className="text-purple-400">Transformar Vidas</span>
            </h2>
            <p className="text-lg text-slate-400 mb-8">
              Las terapias complementarias son fundamentales en la recuperación. 
              Conecta con personas que buscan actividades que complementen su proceso de sanación.
            </p>
            <Button 
              asChild
              size="lg"
              className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-xl px-10 py-6 text-lg font-semibold shadow-lg"
            >
              <Link to="/login">
                Registrar mi Taller Gratis
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
            <p className="text-sm text-slate-500 mt-4">
              100% gratuito • Contacto directo • +30.000 visitas mensuales
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
